// Enum for ticket states
export enum TicketState {
    OPEN = "open",
    IN_PROGRESS = "in-progress",
    RESOLVED = "resolved",
    CLOSED = "closed",
  }
  
  // Enum for ticket priority
  export enum TicketPriority {
    LOW = "low",
    MEDIUM = "medium",
    HIGH = "high",
  }

  // Enum for ticket category
  export enum TicketCategory {
    BUG = "bug",
    FEATURE_REQUEST = "feature-request",
    ACCOUNT = "account",
    BILLING = "billing",
    PERFORMANCE = "performance",
    OTHER = "other",
  }

  // Enum for ticket severity
  export enum TicketSeverity {
    CRITICAL = "critical",
    HIGH = "high",
    MEDIUM = "medium",
    LOW = "low",
  }

  // System context captured at ticket creation
  export interface ISystemContext {
    userAgent?: string; // Full browser user agent string
    browser?: string; // Browser name and version
    os?: string; // Operating system
    deviceType?: string; // "desktop" | "tablet" | "mobile"
    timestamp?: Date; // When context was captured
  }

  // Attachment metadata
  export interface ITicketAttachment {
    id: string; // Unique attachment ID
    filename: string; // Original filename
    mimeType: string; // MIME type
    size: number; // File size in bytes
    uploadedAt: Date; // Upload timestamp
    uploadedBy: string; // User ID who uploaded
    url?: string; // CDN or server URL
  }
  
  // Comment interface
  export interface ISupportTicketComment {
    userId: string; // User who made the comment
    username: string; // Username who made the comment
    userLogo?: string; // User logo
    date: Date; // ISO 8601 timestamp
    text: string; // Comment text
  }

  /**
   * Records a single state transition on a ticket.
   * Stored in stateHistory so the full audit trail is available
   * and can be rendered inline in the comment timeline.
   */
  export interface ITicketStateChange {
    fromState: TicketState;    // State before the transition
    toState: TicketState;      // State after the transition
    changedBy: string;         // Admin user ID who made the change
    changedByUsername: string; // Denormalised username for display
    date: Date;                // ISO 8601 timestamp
  }
  
  /**
   * Represents a single available transition from one ticket state to another,
   * used to drive the workflow action menus in a data-driven (JIRA-style) way.
   */
  export interface ITicketTransition {
    /** Display label for the menu item */
    label: string;
    /** Ionicon name */
    icon: string;
    /** Ionic color token */
    color: string;
    /** 'state' = direct state change; 'resolve-modal' = open the Resolve Ticket modal (captures resolution + KB) */
    action: 'state' | 'resolve-modal';
    /** Target state (only used when action === 'state') */
    targetState?: TicketState;
  }

  /**
   * Constrained ticket workflow (JIRA-style state machine).
   *
   * Allowed transitions:
   *   Open        → Start Progress | Resolve (modal: captures resolution note + KB)
   *   In Progress → Resolve (modal: captures resolution note + KB)
   *   Resolved    → Back to In Progress | Mark Done (plain archive, no modal)
   *   Done/Closed → Reopen (back to Open)
   *
   * Only the transitions listed here appear in the action menu.
   * To change the workflow, edit this table — no other code needs updating.
   */
  export const TICKET_WORKFLOW: Record<TicketState, ITicketTransition[]> = {
    // Open: can start work or jump straight to resolved (modal captures resolution + KB)
    [TicketState.OPEN]: [
      { label: 'Start Progress',   icon: 'time-outline',              color: 'warning', action: 'state',         targetState: TicketState.IN_PROGRESS },
      { label: 'Resolve',          icon: 'checkmark-circle-outline',  color: 'success', action: 'resolve-modal'                                       },
    ],
    // In Progress: must go through Resolve modal (forces a resolution record before closing)
    [TicketState.IN_PROGRESS]: [
      { label: 'Resolve',          icon: 'checkmark-circle-outline',  color: 'success', action: 'resolve-modal'                                       },
    ],
    // Resolved: can go back to in-progress, or archive as Done (plain state change — no modal)
    [TicketState.RESOLVED]: [
      { label: 'Back to In Progress', icon: 'time-outline',           color: 'warning', action: 'state',         targetState: TicketState.IN_PROGRESS },
      { label: 'Mark Done',        icon: 'lock-closed-outline',       color: 'medium',  action: 'state',         targetState: TicketState.CLOSED      },
    ],
    // Done/Closed: can only be reopened
    [TicketState.CLOSED]: [
      { label: 'Reopen',           icon: 'refresh-circle-outline',    color: 'primary', action: 'state',         targetState: TicketState.OPEN        },
    ],
  };

  // Main support ticket interface
  export interface ISupportTicket {
    _id?: string; // MongoDB ID for the ticket
    logId?: string; // Reference to the error log ID
    abstract: string; // Short summary
    description: string; // Full description
    category: TicketCategory; // Ticket category
    severity?: TicketSeverity; // User-assessed severity
    reproducibilitySteps?: string; // Steps to reproduce
    expectedBehavior?: string; // Expected behavior
    actualBehavior?: string; // Actual behavior
    reporter: string; // User who reported the ticket
    assignedTo?: string; // Assigned user
    state: TicketState; // Current state
    priority: TicketPriority; // Support team priority
    comments: ISupportTicketComment[]; // Array of comments
    stateHistory?: ITicketStateChange[];  // Ordered log of every state transition
    attachments?: ITicketAttachment[]; // Array of attachments
    systemContext?: ISystemContext; // Captured system context
    createdAt?: string; // Timestamp (ISO 8601)
    updatedAt?: string; // Timestamp (ISO 8601)
  }